import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Box, Container, Typography, TextField, Button, Paper, Alert, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import SimpleWheelFixed from '../components/SimpleWheelFixed';
import { lookupCity, geocodeCity, getCityList, getUTCOffset } from '../utils/location';

// Helper function to download HTML element as PNG using html2canvas
const downloadElementAsPng = async (element, filename) => {
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: '#0a0a0f',
      useCORS: true,
      logging: false
    });

    const link = document.createElement('a');
    link.download = filename;
    link.href = canvas.toDataURL('image/png');
    link.click();
  } catch (error) {
    console.error('Error generating PNG:', error);
  }
};

// Helper function to get ordinal suffix for house numbers
const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Dark theme colors (consistent with site)
const COLORS = {
  background: '#0a0a0f',
  surface: '#12121a',
  surfaceLight: '#1a1a25',
  accent: '#c9a87c',
  accentDark: '#a8894f',
  text: '#ede8e0',
  textMuted: '#9e9a92',
  border: 'rgba(201, 168, 124, 0.2)',
  borderSolid: '#2a2a35',
};

// Common dark-themed TextField sx
const darkTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    color: COLORS.text,
    backgroundColor: COLORS.surface,
    borderRadius: '8px',
    '& fieldset': { borderColor: COLORS.border },
    '&:hover fieldset': { borderColor: COLORS.accent },
    '&.Mui-focused fieldset': { borderColor: COLORS.accent },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: COLORS.textMuted,
    opacity: 1,
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: COLORS.textMuted,
    color: COLORS.textMuted,
  },
};

const FreeBirthChartPage = () => {
  const navigate = useNavigate();
  // Form state
  const [natalDate, setNatalDate] = useState(null);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [cityName, setCityName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [useManualUtcOffset, setUseManualUtcOffset] = useState(false);
  const [manualUtcOffset, setManualUtcOffset] = useState(0);
  const [planetData, setPlanetData] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [locationWarning, setLocationWarning] = useState('');

  // Refs for download functionality
  const chartContainerRef = useRef(null);

  // The wheel reports its data after every render, in new arrays each time. Only a real change may update this page: otherwise the
  // two re-render each other for as long as a chart is on screen, the browser stays busy and page changes never finish (seen 2026-10-31).
  const handleWheelData = useCallback((data) => {
    setPlanetData((current) => (JSON.stringify(current) === JSON.stringify(data) ? current : data));
  }, []);

  const autoUtcOffset = useMemo(() => {
    if (!natalDate || !timezone) return 0;
    return getUTCOffset(natalDate, timezone);
  }, [natalDate, timezone]);

  const effectiveUtcOffset = useManualUtcOffset ? manualUtcOffset : autoUtcOffset;

  const formatUtcOffset = (offset) => {
    const sign = offset >= 0 ? '+' : '';
    const hours = Math.floor(Math.abs(offset));
    const minutes = Math.round((Math.abs(offset) - hours) * 60);
    if (minutes === 0) {
      return `UTC${sign}${offset}`;
    }
    return `UTC${sign}${offset >= 0 ? '' : '-'}${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  // A slower answer for what was typed earlier must not overwrite the place: "Dubli" arriving after "Dublin" put a Dublin birth in India (seen 2026-10-31)
  const cityLookup = useRef(0);
  const handleCityChange = async (newCityName) => {
    const lookup = ++cityLookup.current;
    setCityName(newCityName);
    // Try static database first (instant)
    const locationData = lookupCity(newCityName);
    if (locationData) {
      setLatitude(locationData.latitude.toString());
      setLongitude(locationData.longitude.toString());
      setTimezone(locationData.timezone);
      setUseManualUtcOffset(false);
      setLocationWarning('');
      return;
    }
    // Try geocoding API if static lookup fails
    if (newCityName.trim().length >= 3) {
      const results = await geocodeCity(newCityName);
      if (lookup !== cityLookup.current) return;
      if (results.length > 0) {
        const best = results[0];
        setLatitude(best.latitude.toString());
        setLongitude(best.longitude.toString());
        setTimezone(best.timezone);
        setUseManualUtcOffset(false);
        setLocationWarning('');
        return;
      }
    }
    if (newCityName.trim()) {
      setLocationWarning('Location not found. Please enter latitude/longitude manually and adjust the UTC offset if needed.');
    }
  };

  const handleCalculate = () => {
    if (!natalDate) {
      alert('Please select a birth date and time');
      return;
    }
    if (!latitude || !longitude) {
      alert('Please enter birth location coordinates');
      return;
    }
    setShowChart(true);
  };

  // Download chart as PNG
  const handleDownloadChart = useCallback(() => {
    if (chartContainerRef.current && natalDate) {
      const dateStr = natalDate.toISOString().split('T')[0];
      downloadElementAsPng(chartContainerRef.current, `birth-chart-${dateStr}.png`);
    }
  }, [natalDate]);

  // The next step after a chart (marketing plan fix 6): the free weekly calendar. Someone with an account goes straight to it;
  // for a new sign-up the birth details travel with the page change (never in the address), so they aren't typed twice.
  const handleOpenCalendar = () => {
    window.scrollTo(0, 0);
    const hasCalendarAccount = (() => {
      try { return !!localStorage.getItem('transit-calendar-token'); } catch { return false; }
    })();
    if (hasCalendarAccount) {
      navigate('/transit-calendar/profiles');
      return;
    }
    // With a hand-set UTC offset or a place typed as coordinates the sign-up couldn't work out the same time zone: it asks for the place itself
    const placeCarries = !useManualUtcOffset && cityName.trim() && timezone !== 'UTC';
    navigate('/transit-calendar/register', {
      state: {
        fromChart: {
          birthTime: natalDate.getTime(),
          ...(placeCarries ? { cityName: cityName.trim(), latitude: parseFloat(latitude), longitude: parseFloat(longitude), timezone } : {}),
        },
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Free Birth Chart Calculator | IvyAstrology</title>
        <meta name="description" content="Calculate your free natal birth chart with our astrological calculator. Get your planetary positions, ascendant, and house placements." />
      </Helmet>

      <Box sx={{ bgcolor: COLORS.background, minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom sx={{ textAlign: 'center', fontWeight: 700, color: COLORS.accent, mb: 4 }}>
            Free Birth Chart Calculator
          </Typography>

          {/* Input Form */}
          <Paper elevation={3} sx={{ p: 4, mb: 4, bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '16px' }}>
            <Grid container spacing={3}>
              {/* Birth Date & Time */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: COLORS.text }}>
                  Birth Date & Time
                </Typography>
                <Box sx={{
                  '& .react-datepicker-wrapper': { width: '100%' },
                  '& .react-datepicker__input-container input': {
                    width: '100%',
                    padding: '16.5px 14px',
                    fontSize: '1rem',
                    backgroundColor: COLORS.surface,
                    color: COLORS.text,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  },
                  '& .react-datepicker__input-container input:focus': {
                    borderColor: COLORS.accent,
                  },
                  '& .react-datepicker__input-container input::placeholder': {
                    color: COLORS.textMuted,
                  },
                }}>
                  <DatePicker
                    selected={natalDate}
                    onChange={(date) => setNatalDate(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    placeholderText="Select date and time"
                    minDate={new Date('1900-01-01')}
                    maxDate={new Date()}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                  />
                </Box>
              </Grid>

              {/* House System (Fixed) */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: COLORS.text }}>
                  House System
                </Typography>
                <TextField
                  fullWidth
                  value="Whole Sign"
                  disabled
                  variant="outlined"
                  sx={darkTextFieldSx}
                />
              </Grid>

              {/* First House Reference (Fixed) */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: COLORS.text }}>
                  First House Reference
                </Typography>
                <TextField
                  fullWidth
                  value="Ascendant (Rising Sign)"
                  disabled
                  variant="outlined"
                  sx={darkTextFieldSx}
                />
              </Grid>

              {/* Birth Location */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: COLORS.text }}>
                  Birth Location
                </Typography>
                <TextField
                  fullWidth
                  value={cityName}
                  onChange={(e) => handleCityChange(e.target.value)}
                  onBlur={(e) => handleCityChange(e.target.value)}
                  placeholder="City, Country (e.g., New York, London)"
                  variant="outlined"
                  sx={darkTextFieldSx}
                  inputProps={{
                    list: 'city-suggestions'
                  }}
                />
                <datalist id="city-suggestions">
                  {getCityList().map(city => (
                    <option key={city} value={city} />
                  ))}
                </datalist>
              </Grid>

              {/* Latitude */}
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: COLORS.text }}>
                  Latitude
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  placeholder="e.g., 40.7128"
                  variant="outlined"
                  sx={darkTextFieldSx}
                  inputProps={{
                    step: '0.0001',
                    min: -90,
                    max: 90
                  }}
                />
              </Grid>

              {/* Longitude */}
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: COLORS.text }}>
                  Longitude
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  placeholder="e.g., -74.0060"
                  variant="outlined"
                  sx={darkTextFieldSx}
                  inputProps={{
                    step: '0.0001',
                    min: -180,
                    max: 180
                  }}
                />
              </Grid>

              {/* UTC Timezone Offset */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: COLORS.surfaceLight, border: `1px solid ${COLORS.border}`, borderRadius: '12px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLORS.text }}>
                      UTC Timezone Offset:
                    </Typography>
                    <Box sx={{
                      px: 2,
                      py: 0.5,
                      bgcolor: 'rgba(201, 168, 124, 0.15)',
                      borderRadius: 1,
                      fontWeight: 'bold',
                      color: COLORS.accent,
                      border: `1px solid ${COLORS.border}`
                    }}>
                      {formatUtcOffset(effectiveUtcOffset)}
                    </Box>
                    <Typography variant="body2" sx={{ color: COLORS.textMuted }}>
                      ({useManualUtcOffset ? 'Manual' : `Auto from ${timezone || 'UTC'}`})
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={useManualUtcOffset}
                        onChange={(e) => {
                          setUseManualUtcOffset(e.target.checked);
                          if (e.target.checked) {
                            setManualUtcOffset(autoUtcOffset);
                          }
                        }}
                        style={{ marginRight: '8px', accentColor: COLORS.accent }}
                      />
                      <Typography variant="body2" sx={{ color: COLORS.text }}>Use Manual UTC Offset</Typography>
                    </label>

                    {useManualUtcOffset && (
                      <TextField
                        select
                        size="small"
                        value={manualUtcOffset}
                        onChange={(e) => setManualUtcOffset(parseFloat(e.target.value))}
                        SelectProps={{
                          native: true,
                        }}
                        sx={{ minWidth: 200, ...darkTextFieldSx, '& select': { color: COLORS.text }, '& option': { backgroundColor: COLORS.surface, color: COLORS.text } }}
                      >
                        <option value={-12}>UTC-12 (Baker Island)</option>
                        <option value={-11}>UTC-11 (American Samoa)</option>
                        <option value={-10}>UTC-10 (Hawaii)</option>
                        <option value={-9}>UTC-9 (Alaska)</option>
                        <option value={-8}>UTC-8 (Pacific Time)</option>
                        <option value={-7}>UTC-7 (Mountain Time)</option>
                        <option value={-6}>UTC-6 (Central Time)</option>
                        <option value={-5}>UTC-5 (Eastern Time)</option>
                        <option value={-4}>UTC-4 (Atlantic Time)</option>
                        <option value={-3.5}>UTC-3:30 (Newfoundland)</option>
                        <option value={-3}>UTC-3 (Buenos Aires)</option>
                        <option value={-2}>UTC-2 (South Georgia)</option>
                        <option value={-1}>UTC-1 (Azores)</option>
                        <option value={0}>UTC+0 (London, GMT)</option>
                        <option value={1}>UTC+1 (Paris, Berlin)</option>
                        <option value={2}>UTC+2 (Bucharest, Athens)</option>
                        <option value={3}>UTC+3 (Moscow, Istanbul)</option>
                        <option value={3.5}>UTC+3:30 (Tehran)</option>
                        <option value={4}>UTC+4 (Dubai)</option>
                        <option value={4.5}>UTC+4:30 (Kabul)</option>
                        <option value={5}>UTC+5 (Pakistan)</option>
                        <option value={5.5}>UTC+5:30 (India)</option>
                        <option value={5.75}>UTC+5:45 (Nepal)</option>
                        <option value={6}>UTC+6 (Bangladesh)</option>
                        <option value={7}>UTC+7 (Bangkok, Jakarta)</option>
                        <option value={8}>UTC+8 (Singapore, Beijing)</option>
                        <option value={9}>UTC+9 (Tokyo, Seoul)</option>
                        <option value={9.5}>UTC+9:30 (Adelaide)</option>
                        <option value={10}>UTC+10 (Sydney)</option>
                        <option value={11}>UTC+11 (Solomon Islands)</option>
                        <option value={12}>UTC+12 (New Zealand)</option>
                      </TextField>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Location Warning */}
              {locationWarning && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    {locationWarning}
                  </Alert>
                </Grid>
              )}

              {/* Calculate Button */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleCalculate}
                    sx={{
                      px: 6,
                      py: 1.5,
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      backgroundColor: COLORS.accent,
                      color: COLORS.background,
                      '&:hover': { backgroundColor: COLORS.accentDark },
                    }}
                  >
                    Calculate Birth Chart
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Chart Display */}
          {showChart && natalDate && latitude && longitude && (
            <Box sx={{ mt: 4 }}>
              {/* Combined Chart Container (Wheel + Planetary Positions) - for single download */}
              <Box
                ref={chartContainerRef}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  gap: 4,
                  background: COLORS.background,
                  p: 3,
                  borderRadius: 2
                }}
              >
                {/* Wheel - larger on desktop/tablet */}
                <Box sx={{
                  width: { xs: '100%', sm: '100%', md: '650px', lg: '750px' },
                  maxWidth: '100%',
                  flexShrink: 0,
                }}>
                  <SimpleWheelFixed
                    natalDate={natalDate}
                    transitDate={natalDate}
                    houseSystem="whole-sign"
                    latitude={parseFloat(latitude)}
                    longitude={parseFloat(longitude)}
                    timezone={timezone}
                    utcOffset={effectiveUtcOffset}
                    firstHouseReference="ascendant"
                    manualFirstHouseSign="Aries"
                    natalPlanetFilter="all"
                    transitPlanetFilter="none"
                    onDataUpdate={handleWheelData}
                  />
                </Box>

                {/* Planetary Positions Table */}
                {planetData && (
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      border: `2px solid ${COLORS.accent}`,
                      bgcolor: COLORS.surface,
                      minWidth: 320,
                      maxWidth: 400,
                      borderRadius: '12px',
                    }}
                  >
                    <Typography variant="h6" sx={{ color: COLORS.accent, mb: 2, textAlign: 'center' }}>
                      Natal Chart - {natalDate.toLocaleDateString()}
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mb: 1, color: COLORS.text }}>Planetary Positions</Typography>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: COLORS.text }}>
                      <thead>
                        <tr style={{ borderBottom: `2px solid ${COLORS.accent}` }}>
                          <th style={{ textAlign: 'left', padding: '6px', color: COLORS.accent }}>Planet</th>
                          <th style={{ textAlign: 'left', padding: '6px', color: COLORS.accent }}>Sign</th>
                          <th style={{ textAlign: 'center', padding: '6px', color: COLORS.accent }}>Position</th>
                          <th style={{ textAlign: 'center', padding: '6px', color: COLORS.accent }}>House</th>
                        </tr>
                      </thead>
                      <tbody>
                        {planetData.ascendant && (
                          <tr style={{ borderBottom: `1px solid ${COLORS.borderSolid}`, backgroundColor: 'rgba(201, 168, 124, 0.1)' }}>
                            <td style={{ padding: '6px', fontWeight: 'bold', color: COLORS.accent }}>{planetData.ascendant.symbol} {planetData.ascendant.name}</td>
                            <td style={{ padding: '6px', fontWeight: 'bold', color: COLORS.accent }}>{planetData.ascendant.zodiacSign}</td>
                            <td style={{ textAlign: 'center', padding: '6px', fontWeight: 'bold', color: COLORS.accent }}>{planetData.ascendant.degree}°{String(planetData.ascendant.minute).padStart(2, '0')}'</td>
                            <td style={{ textAlign: 'center', padding: '6px', fontWeight: 'bold', color: COLORS.accent }}>1st</td>
                          </tr>
                        )}
                        {planetData.natalPositions && planetData.natalPositions.map((planet, index) => (
                          <tr key={index} style={{ borderBottom: `1px solid ${COLORS.borderSolid}` }}>
                            <td style={{ padding: '6px', color: COLORS.text }}>
                              {planet.symbol} {planet.name}
                              {planet.isRetrograde && <span style={{ color: '#e24a4a', marginLeft: '4px', fontSize: '11px' }}>&#8479;</span>}
                            </td>
                            <td style={{ padding: '6px', color: COLORS.text }}>{planet.zodiacSign}</td>
                            <td style={{ textAlign: 'center', padding: '6px', color: COLORS.text }}>{planet.degree}°{String(planet.minute).padStart(2, '0')}'</td>
                            <td style={{ textAlign: 'center', padding: '6px', color: COLORS.text }}>{getOrdinal(planet.house)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Box>

                    {/* Planet Legend */}
                    <Box sx={{ mt: 2, p: 1, bgcolor: COLORS.surfaceLight, borderRadius: 1, border: `1px solid ${COLORS.border}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <Box sx={{ width: 16, height: 16, bgcolor: '#4a90e2', borderRadius: '50%' }} />
                        <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Natal Planets</Typography>
                      </Box>
                    </Box>

                    {/* Aspect Legend */}
                    <Box sx={{ mt: 2, p: 1, bgcolor: COLORS.surfaceLight, borderRadius: 1, border: `1px solid ${COLORS.border}` }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: COLORS.textMuted }}>Aspects:</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 20, height: 2, bgcolor: '#FFBF00' }} />
                          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Conjunction</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 20, height: 2, bgcolor: '#8800FF' }} />
                          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Opposition</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 20, height: 2, bgcolor: '#0000FF' }} />
                          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Trine</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 20, height: 2, bgcolor: '#FF0000' }} />
                          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Square</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 20, height: 2, bgcolor: '#00FF00', borderTop: '1px dashed #00FF00' }} />
                          <Typography variant="caption" sx={{ color: COLORS.textMuted }}>Sextile</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                )}
              </Box>

              {/* Download Chart Button */}
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleDownloadChart}
                  startIcon={<span style={{ fontSize: '16px' }}>&#8681;</span>}
                  sx={{
                    px: 4,
                    borderColor: COLORS.accent,
                    color: COLORS.accent,
                    borderRadius: '12px',
                    textTransform: 'none',
                    '&:hover': { borderColor: COLORS.accentDark, backgroundColor: 'rgba(201, 168, 124, 0.08)' },
                  }}
                >
                  Download Chart as PNG
                </Button>
              </Box>

              {/* What to do with the chart next */}
              <Paper elevation={3} sx={{ mt: 4, p: { xs: 3, sm: 4 }, textAlign: 'center', bgcolor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '16px' }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: COLORS.accent, mb: 1 }}>
                  See when today's planets touch this chart
                </Typography>
                <Typography variant="body1" sx={{ color: COLORS.text, maxWidth: 620, mx: 'auto', mb: 3 }}>
                  Your personal calendar shows the exact days Mars, Jupiter and the other planets aspect this chart, with a Daily Briefing in plain English. The weekly view is free, no card needed.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleOpenCalendar}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1rem',
                    backgroundColor: COLORS.accent,
                    color: COLORS.background,
                    '&:hover': { backgroundColor: COLORS.accentDark },
                  }}
                >
                  See My Free Weekly Calendar
                </Button>
              </Paper>
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default FreeBirthChartPage;
